import { useFormikContext } from 'formik';
import { FC, ReactNode, useRef, useState } from 'react';
import _ from 'lodash';
import { QcsButton } from '../common/basic/QcsButton';
import { useTranslation } from 'react-i18next';
import { BannerInputUploadModal } from './BannerInputUploadModal';
import styled, { CSSObject } from '@emotion/styled';
import { BASE_URL_WITH_VERSION } from '../../utils/api';
import { getMediaToken } from '../../utils/storage';

//The logo frame and the upload button share a column, so the column is sized to whichever is wider
//and both stretch to it. That keeps the frame exactly as wide as the button in every translation,
//without pinning either to a magic number.
const LogoGrid = styled.div(
  ({ theme }): CSSObject => ({
    display: 'grid',
    gridTemplateAreas: '"logo field" "upload action"',
    gridTemplateColumns: 'max-content max-content',
    justifyItems: 'start',
    //Top, not centre: the frame is taller than the field, and centring would drop the field a few
    //pixels below the fields in the neighbouring group.
    alignItems: 'start',
    gap: '1rem',
    [theme.breakpoints.down('sm')]: {
      gridTemplateAreas: '"logo" "field" "upload" "action"',
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  })
);

const LogoCell = styled.div(
  (): CSSObject => ({
    gridArea: 'logo',
    width: '100%',
    minWidth: 0,
  })
);

const FieldCell = styled.div(
  (): CSSObject => ({
    gridArea: 'field',
    width: '100%',
    minWidth: 0,
  })
);

const UploadCell = styled.div(
  (): CSSObject => ({
    gridArea: 'upload',
    width: '100%',
    minWidth: 0,
  })
);

const ActionCell = styled.div(
  (): CSSObject => ({
    gridArea: 'action',
    width: '100%',
    minWidth: 0,
  })
);

//Logos are usually transparent and authored for a white header or e-mail, so they are previewed on
//white. The frame is a landscape rectangle, not a square: a banner is wide and short, and a square
//would frame every real logo with empty margins on both sides.
const LogoStage = styled.div(
  ({ theme }): CSSObject => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    //Exactly the height of an input row, so the frame lines up with the field beside it instead of
    //reading as a field that came out slightly too tall. Fixed, so the row never jumps.
    height: '3.5rem',
    //Tight, so the logo inside gets as close as the frame allows to the 50px the app header
    //actually renders it at.
    padding: '0.25rem',
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  })
);

const LogoImage = styled.img(
  (): CSSObject => ({
    //Fills the frame minus its padding — 48px, near the 50px of the real header logo. The width cap
    //keeps a wide banner from stretching the column, and with it the upload button underneath.
    maxHeight: '3rem',
    maxWidth: '14rem',
    objectFit: 'contain',
  })
);

interface Props {
  onRemoveOldFile: (document: string) => void;
  //Rendered beside the logo. The e-mail height describes the logo, but it belongs to the form, so
  //the field is passed in rather than owned here.
  fieldSlot?: ReactNode;
  //Rendered next to the upload button, so the section has a single row of actions.
  actionSlot?: ReactNode;
}

export const BannerInput: FC<Props> = ({ onRemoveOldFile, fieldSlot, actionSlot }) => {
  const { values, setFieldValue } = useFormikContext<any>();
  const { t } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const mediaToken = getMediaToken();

  const banner = _.get(values, 'banner');
  const bannerUrl = _.get(values, 'bannerUrl');

  const handleUploadFile = () => {
    fileRef.current?.click();
  };

  const handleFileChange = (files: FileList | null) => {
    if (!fileRef.current || !files) {
      return;
    }

    const file = files[0];
    setSelectedFile(file);

    fileRef.current.value = '';
  };

  const handleFileUploaded = (url: string, id: string) => {
    if (!selectedFile) {
      return;
    }

    if (banner) {
      onRemoveOldFile(banner);
    }

    setFieldValue('banner', id);
    setFieldValue('bannerUrl', url);

    setSelectedFile(null);
  };

  const handleFileCancel = () => {
    setSelectedFile(null);
  };

  return (
    <>
      {/* The heading is supplied by the surrounding FormGroup. */}
      <LogoGrid>
        <LogoCell>
          <LogoStage>{!!bannerUrl && <LogoImage alt="" src={`${BASE_URL_WITH_VERSION}${bannerUrl}?token=${mediaToken}`} />}</LogoStage>
        </LogoCell>
        <FieldCell>{fieldSlot}</FieldCell>
        <UploadCell>
          <QcsButton onClick={handleUploadFile} variant="outlined" fullWidth>
            {t('common.uploadFile')}
          </QcsButton>
        </UploadCell>
        <ActionCell>{actionSlot}</ActionCell>
      </LogoGrid>
      <BannerInputUploadModal file={selectedFile} onFileUploaded={handleFileUploaded} onCloseOnError={handleFileCancel} />
      <input type="file" ref={fileRef} onChange={({ target }) => handleFileChange(target.files)} hidden />
    </>
  );
};
